import type { ComponentPropsWithoutRef } from "react";
import styles from "./StatusDot.module.css";

/**
 * StatusDot — 8px indicator dot primitive.
 *
 * Matches the Figma StatusDot component (fileKey QkVMIm6TUdblppfqYlUtai,
 * node 1016:1492). A standalone colored circle used to indicate
 * connection / presence / sync status. Intended to sit alongside a
 * text label (e.g. in a HeaderBar next to a device name, or beside
 * a Notification's app name in the unread state) — it's NOT a Badge
 * (which has its own pill shape and lives as a separate primitive).
 *
 * Tone: Figma ships only the green variant, which stays the DEFAULT so
 * every existing call site (notably `Notification`'s unread dot) is
 * untouched. The other two tones exist because `HeaderBar` had a real
 * three-state connection value it could only express as `sr-only` text —
 * the dot beside it was hardcoded green in every state, so sighted users
 * had no way to learn that mirroring had stopped. Green also already
 * means "connected" on the PC surfaces, which is why the clipboard card
 * language deliberately avoided a mint accent.
 *
 * Accessibility:
 *   - Decorative by default — the dot alone isn't meaningful, it
 *     sits next to visible text that describes the state ("Connected",
 *     "Syncing", etc.). When used this way, omit `label` and the
 *     wrapper renders `aria-hidden="true"` so screen readers skip it
 *     and announce only the visible text.
 *   - When the dot is the ONLY indicator of state (compact UIs, no
 *     visible label), pass `label` — the wrapper renders
 *     `role="img"` + `aria-label` and assistive tech announces the
 *     current state. Matches Icon's label/decorative pattern exactly
 *     (Icon also uses `role="img"` for labeled static visuals, not
 *     `role="status"` which is an ARIA live region for dynamic
 *     announcements like toasts or Spinner).
 *   - `tone` and `pulse` carry NO accessible meaning on their own —
 *     colour and motion are never the sole channel. Every consumer
 *     pairs them with text (visible or `sr-only`).
 */

/**
 * `positive` — healthy / connected (the Figma green, and the default).
 * `warning` — degraded but self-healing, e.g. reconnecting.
 * `neutral` — inactive, or not yet known.
 */
export type StatusDotTone = "positive" | "warning" | "neutral";

export type StatusDotProps = {
  /** Accessible label. When set, the dot announces as `role="img"`
   *  + `aria-label`. When omitted, the dot is decorative and carries
   *  `aria-hidden="true"`. */
  label?: string;
  /** Visual tone. Defaults to `positive` so existing call sites are unchanged. */
  tone?: StatusDotTone;
  /** Slowly pulse the dot. Opt-in per call site rather than implied by
   *  `tone`, so a future static warning dot doesn't inherit motion it
   *  didn't ask for. Respects `prefers-reduced-motion`. */
  pulse?: boolean;
  className?: string;
} & Omit<
  ComponentPropsWithoutRef<"span">,
  "className" | "children" | "aria-label" | "role" | "aria-hidden"
>;

export function StatusDot({
  label,
  tone = "positive",
  pulse = false,
  className,
  ...rest
}: StatusDotProps) {
  const classes = [styles.statusDot, styles[tone], pulse ? styles.pulse : null, className]
    .filter(Boolean)
    .join(" ");
  const a11y = label ? { role: "img" as const, "aria-label": label } : { "aria-hidden": true };
  // `data-tone` / `data-pulse` mirror the visual state into the DOM: CSS
  // Modules resolve to empty strings under this package's vitest config, so
  // class names are not assertable, and the whole point of this component is
  // that its rendered state must be verifiable against the label beside it.
  return (
    <span
      className={classes}
      data-tone={tone}
      {...(pulse ? { "data-pulse": "true" } : {})}
      {...a11y}
      {...rest}
    />
  );
}
